import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Check, Volume2, VolumeX, ArrowLeft } from "lucide-react";
import {
  playSound,
  SOUND_KEYS,
  getPreferredSound,
  setPreferredSound,
  isSoundEnabled,
  setSoundEnabled,
} from "@/lib/sounds";

const OPTIONS = [
  {
    key: SOUND_KEYS.voz,
    name: 'ColegaLink · Firma + Voz "Colega Link"',
    tag: "Recomendado",
    desc: 'Firma sonora oficial de ColegaLink con la voz "Colega Link" incluida.',
    accent: "from-emerald-500/20 to-teal-500/10 border-emerald-500/40",
  },
  {
    key: SOUND_KEYS.vozOff,
    name: "ColegaLink · Firma sonora (sin voz)",
    tag: "Discreto",
    desc: "Solo el chime instrumental, sin voz. Ideal para entornos silenciosos.",
    accent: "from-sky-500/20 to-indigo-500/10 border-sky-500/40",
  },
];

export default function PreviewSonidos() {
  const [selected, setSelected] = useState(getPreferredSound());
  const [enabled, setEnabled] = useState(isSoundEnabled());
  const [saved, setSaved] = useState(false);

  const handlePlay = (key) => playSound(key);

  const handleSelect = (key) => {
    setSelected(key);
    setPreferredSound(key);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    setSoundEnabled(next);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/app"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 mb-6"
          data-testid="preview-sonidos-back"
        >
          <ArrowLeft size={16} /> Volver al panel
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Volume2 className="text-emerald-400" size={28} />
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            Sonidos de <span className="text-emerald-400">ColegaLink</span>
          </h1>
        </div>
        <p className="text-slate-400 mb-6 max-w-2xl">
          Elige el sonido que quieres escuchar al recibir notificaciones
          (nuevas mediaciones, asignaciones, mensajes, pagos, cambios de estado).
        </p>

        <button
          onClick={toggleEnabled}
          className={`mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition ${
            enabled
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/20"
              : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"
          }`}
          data-testid="preview-sonidos-toggle-enabled"
        >
          {enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          {enabled ? "Sonido de notificaciones activado" : "Sonido silenciado"}
        </button>

        {saved && (
          <div
            className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-200 text-sm flex items-center gap-2"
            data-testid="preview-sonidos-saved-toast"
          >
            <Check size={16} /> Preferencia guardada. Este sonido se usará en las notificaciones.
          </div>
        )}

        <div className="space-y-4">
          {OPTIONS.map((o) => {
            const isSel = selected === o.key;
            return (
              <div
                key={o.key}
                className={`rounded-2xl border bg-gradient-to-br ${o.accent} p-5 backdrop-blur transition-all ${
                  isSel ? "ring-2 ring-emerald-400/60" : ""
                }`}
                data-testid={`preview-sonidos-card-${o.key}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold">{o.name}</h3>
                      <span className="text-[10px] uppercase tracking-widest bg-black/30 border border-white/10 px-2 py-0.5 rounded-full">
                        {o.tag}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mt-1">{o.desc}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handlePlay(o.key)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-medium transition"
                      data-testid={`preview-sonidos-play-${o.key}`}
                    >
                      <Play size={16} /> Reproducir
                    </button>
                    <button
                      onClick={() => handleSelect(o.key)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                        isSel
                          ? "bg-emerald-500 text-slate-950 border-emerald-500"
                          : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/20"
                      }`}
                      data-testid={`preview-sonidos-select-${o.key}`}
                    >
                      {isSel ? (
                        <>
                          <Check size={16} /> Elegido
                        </>
                      ) : (
                        "Elegir este"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-sm text-slate-400">
          <p className="font-medium text-slate-200 mb-1">Notas</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Solo estos 2 sonidos son oficiales de ColegaLink.</li>
            <li>Si tu dispositivo tiene el sonido silenciado, no escucharás nada. Sube el volumen y vuelve a probar.</li>
            <li>En iOS/Safari es necesario que hagas <em>clic</em> al menos una vez para habilitar el audio (limitación del navegador).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
